import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button, Card } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { cancelMeeting, listMeetings } from '@/data/client';
import type { Meeting } from '@/data/types';
import { useMe } from '@/auth/useMe';
import { FormError, Modal } from './Modal';
import { ScheduleMeetingModal, type GuestOption } from './ScheduleMeeting';

const when = (m: Meeting, tz: string) => new Intl.DateTimeFormat('en-US', {
  timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}).format(new Date(m.startAt));

/* Upcoming meetings for a company, deal or contact, with a Schedule button. */
export function MeetingsPanel({ filter, guests, defaultTitle, companyId, dealId }: {
  filter: { companyId?: string; dealId?: string; contactId?: string };
  guests: GuestOption[]; defaultTitle: string; companyId?: string; dealId?: string;
}) {
  const { me } = useMe();
  const [scheduling, setScheduling] = useState(false);
  const { data = [] } = useQuery({ queryKey: ['meetings', filter], queryFn: () => listMeetings(filter) });
  const upcoming = data.filter((m) => Date.parse(m.endAt) > Date.now());
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head">
        <h3>Meetings</h3>
        <Button variant="ghost" size="sm" iconLeft={<Icon name="calendar" size={14} />} onClick={() => setScheduling(true)}>Schedule</Button>
      </div>
      {upcoming.length ? <MeetingList meetings={upcoming} tz={me?.workspace.timezone ?? 'America/Chicago'} /> : <p className="crm-cell-muted">Nothing scheduled.</p>}
      {scheduling && (
        <ScheduleMeetingModal companyId={companyId} dealId={dealId} guests={guests} defaultTitle={defaultTitle} onClose={() => setScheduling(false)} />
      )}
    </Card>
  );
}

export function MeetingList({ meetings, tz, showAccount }: { meetings: Meeting[]; tz: string; showAccount?: boolean }) {
  const qc = useQueryClient();
  const { me } = useMe();
  const [confirm, setConfirm] = useState<Meeting | null>(null);
  const cancel = useMutation({
    mutationFn: (id: string) => cancelMeeting(id),
    onSuccess: () => { setConfirm(null); for (const k of ['meetings', 'company', 'deal', 'contact']) void qc.invalidateQueries({ queryKey: [k] }); },
  });
  return (
    <>
      <ul className="crm-people">
        {meetings.map((m) => (
          <li key={m.id}>
            <div>
              <strong>{m.title}</strong>
              <span className="crm-cell-sub">
                {when(m, tz)} · {m.attendees.length} people{showAccount && m.companyName ? <> · <Link to={`/companies/${m.companyId}`}>{m.companyName}</Link></> : null}
              </span>
            </div>
            <span className="crm-record__actions">
              {m.meetUrl && <a className="crm-meet" href={m.meetUrl} target="_blank" rel="noreferrer"><Icon name="play" size={12} /> Meet</a>}
              {m.organizerUserId === me?.userId && (
                <button type="button" className="lead-modal__x" aria-label={`Cancel ${m.title}`} title="Cancel meeting" onClick={() => setConfirm(m)}>
                  <Icon name="x" size={14} />
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
      {confirm && (
        <Modal title={`Cancel “${confirm.title}”?`} onClose={() => setConfirm(null)} footer={<>
          <Button variant="ghost" onClick={() => setConfirm(null)}>Keep it</Button>
          <Button variant="danger" onClick={() => cancel.mutate(confirm.id)} disabled={cancel.isPending}>Cancel meeting</Button>
        </>}>
          <p>Google will email every attendee that the meeting is cancelled.</p>
          <FormError error={cancel.error} />
        </Modal>
      )}
    </>
  );
}
